// V14.13.9 — Fullscreen media lightbox for posts.
//
// Tap or long-press (≥600 ms) on a post's image/video → opens a fullscreen
// preview with prev/next nav and a close button. Works on guest link AND
// authenticated MP views. Supports multi-attachment posts (swipe through
// all `mediaList` items).
//
// Used by `PostMedia` in `SocialPostTemplates.js` and by the news article
// hero/inline images. Closes on Esc, backdrop click, or close button.
import React, { useState, useEffect, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight, Play } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const isVideoUrl = (url) => {
  if (!url) return false;
  const ext = url.split('?')[0].split('.').pop().toLowerCase();
  return ['mp4', 'webm', 'ogg', 'mov', 'avi', 'mkv'].includes(ext);
};

const resolveUrl = (url) => (url?.startsWith('/') ? `${BACKEND_URL}${url}` : url);

const MediaLightbox = ({ mediaList = [], startIndex = 0, onClose }) => {
  const [idx, setIdx] = useState(startIndex);

  useEffect(() => { setIdx(startIndex); }, [startIndex]);

  const next = useCallback(() => setIdx(i => (i + 1) % mediaList.length), [mediaList.length]);
  const prev = useCallback(() => setIdx(i => (i - 1 + mediaList.length) % mediaList.length), [mediaList.length]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [next, prev, onClose]);

  if (!mediaList.length) return null;
  const url = mediaList[idx];
  const isVid = isVideoUrl(url);
  const resolved = resolveUrl(url);

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/95 backdrop-blur-sm flex items-center justify-center animate-in fade-in duration-150"
      onClick={onClose}
      data-testid="media-lightbox-overlay"
      role="dialog"
      aria-modal="true"
    >
      {/* Close */}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClose?.(); }}
        className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
        aria-label="Close preview"
        data-testid="media-lightbox-close"
      >
        <X className="w-5 h-5" />
      </button>

      {/* Counter */}
      {mediaList.length > 1 && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 text-white/80 text-sm bg-white/10 rounded-full px-3 py-1">
          {idx + 1} / {mediaList.length}
        </div>
      )}

      {/* Prev */}
      {mediaList.length > 1 && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); prev(); }}
          className="absolute left-4 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
          aria-label="Previous"
          data-testid="media-lightbox-prev"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
      )}

      {/* Media */}
      <div
        className="max-w-[92vw] max-h-[88vh] flex items-center justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        {isVid ? (
          <video
            key={resolved}
            src={resolved}
            className="max-w-full max-h-[88vh] rounded-lg shadow-2xl"
            controls
            autoPlay
            playsInline
            data-testid="media-lightbox-video"
          />
        ) : (
          <img
            key={resolved}
            src={resolved}
            alt=""
            className="max-w-full max-h-[88vh] object-contain rounded-lg shadow-2xl"
            data-testid="media-lightbox-image"
          />
        )}
      </div>

      {/* Next */}
      {mediaList.length > 1 && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); next(); }}
          className="absolute right-4 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
          aria-label="Next"
          data-testid="media-lightbox-next"
        >
          <ChevronRight className="w-6 h-6" />
        </button>
      )}
    </div>
  );
};

/**
 * Hook returning `{ open, Lightbox }`.
 *   `open(mediaList, startIndex=0)` — open the lightbox
 *   `Lightbox` — render this once anywhere in the tree
 *
 * V14.13.9 — kept hook + component co-located to make drop-in usage trivial.
 */
export const useMediaLightbox = () => {
  const [state, setState] = useState({ open: false, list: [], idx: 0 });
  const open = useCallback((list, idx = 0) => {
    if (!list?.length) return;
    setState({ open: true, list, idx });
  }, []);
  const close = useCallback(() => setState(s => ({ ...s, open: false })), []);

  const Lightbox = state.open ? (
    <MediaLightbox mediaList={state.list} startIndex={state.idx} onClose={close} />
  ) : null;

  return { open, Lightbox };
};

/**
 * Wrapper that handles short-tap AND long-press (≥600 ms) on a media tile,
 * both of which trigger `onActivate(index)`. Use this around any post media
 * thumbnail to enable lightbox preview without rewriting the host markup.
 */
export const LongPressMediaTile = ({
  index = 0, onActivate, longPressMs = 600, children, className = '',
}) => {
  // Refs avoid stale closures when timer fires.
  const stateRef = React.useRef({ timer: null, didLongPress: false });

  const start = (e) => {
    // Prevent native context menu on long touch.
    if (e?.cancelable) e.preventDefault?.();
    stateRef.current.didLongPress = false;
    stateRef.current.timer = setTimeout(() => {
      stateRef.current.didLongPress = true;
      onActivate?.(index);
    }, longPressMs);
  };
  const cancel = () => {
    if (stateRef.current.timer) {
      clearTimeout(stateRef.current.timer);
      stateRef.current.timer = null;
    }
  };
  const click = (e) => {
    cancel();
    // Suppress if long-press already fired the open (avoids double-open).
    if (stateRef.current.didLongPress) {
      stateRef.current.didLongPress = false;
      return;
    }
    onActivate?.(index);
  };

  return (
    <div
      className={`${className} cursor-zoom-in select-none`}
      onClick={click}
      onMouseDown={start}
      onMouseUp={cancel}
      onMouseLeave={cancel}
      onTouchStart={start}
      onTouchEnd={cancel}
      onTouchCancel={cancel}
      role="button"
      tabIndex={0}
      data-testid="media-tile-pressable"
    >
      {children}
    </div>
  );
};

export default MediaLightbox;
export { isVideoUrl, resolveUrl, Play };
